import { FormProvider, useForm } from "react-hook-form";
import { DocumentFormProps } from "./ValidationModal";
import { Box, Stack, TextField, Button, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import yup from "core/validation/yup-config";
import FieldDatepicker from "components/forms/fields/FieldDatepicker";
import { useMemo } from "react";
import { useGetValueOptions } from "core/value_options/hooks";
import FieldAutocompleteList from "components/forms/fields/FieldAutocompleteList";
import { countryOption } from "pages/main/candidates/constants";

const schema = yup.object().shape({
  document_type: yup.string().required(),
  department: yup.string().required(),
  last_name: yup.string().required(),
  first_name: yup.string().required(),
  gender: yup.string().required(),
  birth_date: yup.string().required(),
  birth_place: yup.string().required(),
  birth_department: yup.string().required(),
  birth_country: yup.string().required(),
  expiry_date: yup.string().required(),
  issue_date: yup.string().required(),
  nationality: yup.string().required(),
  document_number: yup.string(),
  issuing_country: yup.string().required(),
});

const IdentityDocumentForm = ({ document, onApprove, onReject, disabled }: DocumentFormProps) => {
  const { data: documentTypes } = useGetValueOptions({
    namespace: 'anael',
    category: 'document_id',
  });

  const methods = useForm<any>({
    mode: 'onBlur',
    defaultValues: document.ocr_data,
    resolver: yupResolver(schema)
  });
  const { register, formState: { errors }, handleSubmit, watch, control } = methods;

  const documentTypeOptions = useMemo(() => {
    if (!documentTypes) return [];

    const options = documentTypes.options.map((type: any) => [type.value, type.label]);
    options.unshift(['', '']);

    return options;
  }, [documentTypes]);

  const genderOptions = useMemo(() => {
    return ['M', 'F'];
  }, []);

  const isMinor = new Date(watch('birth_date')) > new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000);

  return (
    <Box sx={{ height: '100%' }}>
      <FormProvider {...methods}>
        <Stack justifyContent="space-between" height="90%">
          <Stack gap={2} mb={2}>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Box width="100%">
                <FieldAutocompleteList
                  control={control}
                  options={documentTypeOptions}
                  fieldName="document_type"
                  label="Type de document"
                  labelId="document_type"
                  disabled={disabled}
                  sx={{ backgroundColor: 'white' }}
                />
              </Box>
              <TextField disabled={disabled} sx={{ backgroundColor: 'white' }} label="Département (adresse) *" {...register('department')} error={!!errors.department} />
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <TextField disabled={disabled} sx={{ backgroundColor: 'white' }} label="Nom *" {...register('last_name')} error={!!errors.last_name} />
              <TextField disabled={disabled} sx={{ backgroundColor: 'white' }} label="Prénom *" {...register('first_name')} error={!!errors.first_name} />
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Box width="100%">
                <FieldAutocompleteList
                  control={control}
                  options={genderOptions}
                  fieldName="gender"
                  label="Sexe *"
                  labelId="gender"
                  disabled={disabled}
                  sx={{ backgroundColor: 'white' }}
                />
              </Box>
              <FieldDatepicker disabled={disabled} fieldName="birth_date" label="Né(e) le *" control={control} sx={{ backgroundColor: 'white' }} />
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <TextField disabled={disabled} sx={{ backgroundColor: 'white' }} label="Département de naissance *" {...register('birth_department')} error={!!errors.birth_department} />
              <TextField disabled={disabled} sx={{ backgroundColor: 'white' }} label="Lieu de naissance *" {...register('birth_place')} error={!!errors.birth_place} />
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <FieldAutocompleteList
                disabled={disabled}
                control={control}
                options={countryOption}
                fieldName='birth_country'
                labelId='birth_country'
                label="Pays de naissance"
                sx={{ backgroundColor: 'white' }}
                containerSx={{ width: '100%' }}
              />
              <FieldAutocompleteList
                disabled={disabled}
                control={control}
                options={countryOption}
                fieldName='nationality'
                labelId='nationality'
                label="Nationalité"
                sx={{ backgroundColor: 'white' }}
                containerSx={{ width: '100%' }}
              />
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <FieldAutocompleteList
                disabled={disabled}
                control={control}
                options={countryOption}
                fieldName='issuing_country'
                labelId='issuing_country'
                label="Pays émetteur"
                sx={{ backgroundColor: 'white' }}
                containerSx={{ width: '100%' }}
              />
              <TextField disabled={disabled} sx={{ backgroundColor: 'white' }} label="Numéro de document" {...register('document_number')} />
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <FieldDatepicker disabled={disabled} label="Valable jusqu'à *" control={control} fieldName="expiry_date" sx={{ backgroundColor: 'white' }} />
              <FieldDatepicker disabled={disabled} label="Délivrée le *" control={control} fieldName="issue_date" sx={{ backgroundColor: 'white' }} />
            </Stack>
            {isMinor && (
              <Stack>
                <Typography sx={{ fontSize: '14px', color: '#aa0000', fontWeight: 'bold' }}>
                  ⚠️ Attention : personne mineure détectée
                </Typography>
                <Typography sx={{ fontSize: '14px', color: 'red' }}>
                  L'âge calculé est inférieur à 18 ans.
                </Typography>
              </Stack>
            )}
          </Stack>

          {!disabled && (
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Button variant="outlined" color="error" onClick={onReject}>Refuser le document</Button>
              <Button variant="contained" color="primary" onClick={handleSubmit(onApprove)}>Valider</Button>
            </Stack>
          )}
        </Stack>
      </FormProvider>
    </Box>
  );
};

export default IdentityDocumentForm;